import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { userService } from '../../_services';

function BrokerSignature({
    text,
    closeBrokerPopup,
    openManageGroup,
    openEditGroup,
    GroupNumber,
    MaxComm,
    cPagin,
    cSearch,
    userLists
}) {
    const [loading, setLoading] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Form fields
    const [partnerName, setPartnerName] = useState('');
    const [partnerPhone, setPartnerPhone] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    
    // Field errors
    const [errors, setErrors] = useState({
        name: '',
        phone: '',
        company: '',
        address1: '',
        address2: ''
    });

    const clearError = useCallback((field) => {
        setErrors(prev => ({ ...prev, [field]: '' }));
        setError('');
    }, []);

    const handleSubmit = useCallback(async () => {
        setLoading(true);
        setError('');
        
        const newErrors = {
            name: '',
            phone: '',
            company: '',
            address1: '',
            address2: ''
        };
        let hasError = false;

        // Validate partner name
        if (!partnerName || userService.Check_Null_Empty(partnerName)) {
            newErrors.name = "Partner name can't be blank";
            hasError = true;
        } else if (userService.ValidateSpace(partnerName)) {
            newErrors.name = 'Space not acceptable';
            hasError = true;
        }

        // Validate phone
        if (!partnerPhone || userService.Check_Null_Empty(partnerPhone)) {
            newErrors.phone = "Partner phone can't be blank";
            hasError = true;
        } else if (userService.ValidateSpace(partnerPhone)) {
            newErrors.phone = 'Space not acceptable';
            hasError = true;
        } else if (!userService.validateNumber(partnerPhone)) {
            newErrors.phone = 'Invalid phone number';
            hasError = true;
        }

        // Validate company name
        if (!companyName || userService.Check_Null_Empty(companyName)) {
            newErrors.company = "Company name can't be blank";
            hasError = true;
        } else if (userService.ValidateSpace(companyName)) {
            newErrors.company = 'Space not acceptable';
            hasError = true;
        }

        // Validate address line 1
        if (!addressLine1 || userService.Check_Null_Empty(addressLine1)) {
            newErrors.address1 = "Address Line1 can't be blank";
            hasError = true;
        } else if (userService.ValidateSpace(addressLine1)) {
            newErrors.address1 = 'Space not acceptable';
            hasError = true;
        }

        // Validate address line 2
        if (!addressLine2 || userService.Check_Null_Empty(addressLine2)) {
            newErrors.address2 = "Address Line2 can't be blank";
            hasError = true;
        } else if (userService.ValidateSpace(addressLine2)) {
            newErrors.address2 = 'Space not acceptable';
            hasError = true;
        }

        setErrors(newErrors);

        if (hasError) {
            setLoading(false);
            return;
        }

        try {
            const apidata = await userService.BrokerSignatureSetting(
                GroupNumber,
                partnerName,
                partnerPhone,
                companyName,
                addressLine1,
                addressLine2
            );

            if (apidata?.length > 0) {
                if (apidata[0].code === '1') {
                    setSuccessMessage(apidata[0].message);
                    setError('');
                    setSubmitDisabled(true);
                    setTimeout(() => {
                        closeBrokerPopup?.(GroupNumber);
                        openManageGroup?.(GroupNumber, MaxComm, cPagin, cSearch);
                    }, 2000);
                } else {
                    setError(apidata[0].message);
                    setSuccessMessage('');
                }
            } else {
                setError('Oops! Something went wrong. Try Again.');
                setSuccessMessage('');
            }
        } catch (err) {
            setError('Oops! Something went wrong. Try Again.');
        } finally {
            setLoading(false);
        }
    }, [partnerName, partnerPhone, companyName, addressLine1, addressLine2, GroupNumber, MaxComm, cPagin, cSearch, closeBrokerPopup, openManageGroup]);

    const handleBack = useCallback(() => {
        closeBrokerPopup?.(GroupNumber);
        openEditGroup?.('', '', 'yes', userLists);
    }, [closeBrokerPopup, openEditGroup, GroupNumber, userLists]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            handleBack();
        }
    }, [handleBack]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Partner Name */}
                        <div className="space-y-2">
                            <Label htmlFor="partnerName">Partner Name</Label>
                            <Input
                                id="partnerName"
                                type="text"
                                value={partnerName}
                                onChange={(e) => {
                                    setPartnerName(e.target.value);
                                    clearError('name');
                                }}
                                placeholder="Enter partner name"
                            />
                            {errors.name && (
                                <p className="text-sm text-destructive">{errors.name}</p>
                            )}
                        </div>

                        {/* Partner Phone */}
                        <div className="space-y-2">
                            <Label htmlFor="partnerPhone">Partner Phone</Label>
                            <Input
                                id="partnerPhone"
                                type="text"
                                value={partnerPhone}
                                onChange={(e) => {
                                    setPartnerPhone(e.target.value);
                                    clearError('phone');
                                }}
                                placeholder="Enter phone number"
                            />
                            {errors.phone && (
                                <p className="text-sm text-destructive">{errors.phone}</p>
                            )}
                        </div>

                        {/* Company Name */}
                        <div className="space-y-2">
                            <Label htmlFor="companyName">Company Name</Label>
                            <Input
                                id="companyName"
                                type="text"
                                value={companyName}
                                onChange={(e) => {
                                    setCompanyName(e.target.value);
                                    clearError('company');
                                }}
                                placeholder="Enter company name"
                            />
                            {errors.company && (
                                <p className="text-sm text-destructive">{errors.company}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Address Line 1 */}
                        <div className="space-y-2">
                            <Label htmlFor="address1">Address Line 1</Label>
                            <Input
                                id="address1"
                                type="text"
                                value={addressLine1}
                                onChange={(e) => {
                                    setAddressLine1(e.target.value);
                                    clearError('address1');
                                }}
                                placeholder="Enter address line 1"
                            />
                            {errors.address1 && (
                                <p className="text-sm text-destructive">{errors.address1}</p>
                            )}
                        </div>

                        {/* Address Line 2 */}
                        <div className="space-y-2">
                            <Label htmlFor="address2">Address Line 2</Label>
                            <Input
                                id="address2"
                                type="text"
                                value={addressLine2}
                                onChange={(e) => {
                                    setAddressLine2(e.target.value);
                                    clearError('address2');
                                }}
                                placeholder="Enter address line 2"
                            />
                            {errors.address2 && (
                                <p className="text-sm text-destructive">{errors.address2}</p>
                            )}
                        </div>
                    </div>

                    {/* Success Message */}
                    {successMessage && (
                        <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm border border-green-200">
                            {successMessage}
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <p className="text-sm text-destructive">
                            {error}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={handleSubmit} disabled={loading || submitDisabled}>
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save & Submit'
                        )}
                    </Button>
                    <Button variant="secondary" onClick={handleBack}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BrokerSignature;
